import type { PropsWithChildren } from 'react'
import { memo } from 'react'

import type { GetStaticPropsResult } from '@/components/Templates/Works/Single/_hook'
import { ArrowLink } from '@/components/UIParts/ArrowLink'
import { Button } from '@/components/UIParts/Button'
import { Heading } from '@/components/UIParts/Heading'
import { pagesPath } from '@/lib/$path'
import { NextLink } from '@/lib/next'
import HeadingAttendImage from '~/images/Utils/heading--attend.svg'

import type { Work } from '../types/work'
import { WorksList } from './WorksList'

type Props = GetStaticPropsResult

export const WorkOverview = memo(({ work, otherWorks }: Props) => {
  return (
    <div>
      <div className="mb-12 flex flex-wrap gap-x-12 gap-y-8 px-6 md:flex-nowrap md:px-0">
        <div className="md:w-4/12">
          <WorkAside {...work} />
        </div>
        <div className="w-full md:w-8/12">
          <WorkDetail {...work} />
        </div>
      </div>
      <div className="mb-16 px-6 md:mb-40 md:px-0">
        <WorkImages {...work} />
      </div>
      <div>
        <Heading main="Other" sub="他の実績" className="mb-10" />
        <WorksList items={otherWorks} />
        <div className="mt-10">
          <div className="mx-auto w-60">
            <NextLink href={pagesPath.works.$url()} passHref>
              <Button as="a" size="medium" theme="secondary">
                実績 一覧
              </Button>
            </NextLink>
          </div>
        </div>
      </div>
    </div>
  )
})

const WorkAside = memo((work: Work) => {
  return (
    <aside className="grid">
      <div>
        <img
          src={work.thumbnail.url}
          width={work.thumbnail.width}
          height={work.thumbnail.height}
          alt={work.client_name}
        />
      </div>
      <div className="mt-4">
        <p className="mb-3 text-xs font-bold">{work.task_name}</p>
        <h1 className="text-xl font-bold">{work.client_name} 様</h1>
      </div>
      {work.url && (
        <div className="mt-6 md:mt-10">
          <ArrowLink href={work.url} target="_blank" rel="noopener noreferrer">
            制作サイト/コンテンツを見る
          </ArrowLink>
        </div>
      )}
    </aside>
  )
})

const DetailHeading = memo<PropsWithChildren<JSX.IntrinsicElements['h3']>>(
  ({ children, className = '', ...props }) => {
    return (
      <h3 className={`flex items-center gap-x-2 text-sm font-bold ${className}`} {...props}>
        <span className="inline-block w-6 text-disabled">
          <HeadingAttendImage />
        </span>
        <span>{children}</span>
      </h3>
    )
  },
)

const DetailItem = memo<
  PropsWithChildren<{
    label: string
  }>
>(({ label, children }) => {
  return (
    <div className="flex md:items-center">
      <div className="w-5/12 md:w-3/12">
        <DetailHeading className="md:mb-2">{label}</DetailHeading>
      </div>
      <div className="w-7/12 text-sm">{children}</div>
    </div>
  )
})

const WorkDetail = memo((work: Work) => {
  return (
    <div className="grid gap-y-8 md:gap-y-10">
      <div>
        <DetailHeading className="mb-2">プロジェクト概要</DetailHeading>
        <div
          className="bg-bg-gray p-6 text-sm leading-loose"
          dangerouslySetInnerHTML={{ __html: work.details.overview }}
        />
      </div>
      {work.details.duration && (
        <DetailItem label="対応期間">
          <p>{work.details.duration}</p>
        </DetailItem>
      )}

      {work.details.responsibility_area && (
        <DetailItem label="担当領域">
          <p>{work.details.responsibility_area}</p>
        </DetailItem>
      )}

      {work.details.function && (
        <DetailItem label="機能">
          <p>{work.details.function}</p>
        </DetailItem>
      )}

      {work.details.function_description && (
        <DetailItem label="機能説明">
          <p>{work.details.function_description}</p>
        </DetailItem>
      )}

      {work.details.device && (
        <DetailItem label="デバイス">
          <p>{work.details.device}</p>
        </DetailItem>
      )}
    </div>
  )
})

const WorkImages = memo((work: Work) => {
  return (
    <div>
      <div className="grid gap-10 bg-bg-gray p-4 md:p-16">
        {work.overview_image.map((item, index) => {
          return (
            <div key={index}>
              <img
                src={item.url}
                width={item.width}
                height={item.height}
                alt={work.client_name}
                className="mx-auto"
              />
            </div>
          )
        })}
      </div>
      {work.notes && <p className="mt-2 text-xs font-bold text-attend">{work.notes}</p>}
    </div>
  )
})
